import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft, 
  X,
  Target,
  Brain,
  Sparkles,
  Heart,
  BarChart3,
  BookOpen,
  TrendingUp,
  Zap,
  Users,
  Share2,
  Smile,
  Sprout
} from 'lucide-react';

const OnboardingFlow = ({ isOpen, onComplete, onSkip, userName, userPhoto }) => {
  // Get first name from full name
  const firstName = userName?.split(" ")[0] || "Friend";
  
  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };
  const [currentScreen, setCurrentScreen] = useState(0);
  const [selections, setSelections] = useState({
    intent: null,
    experience: null,
    behavior: null
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Determine personality type based on behavior selection
  const determinePersonality = useCallback((behavior) => {
    const personalityMap = {
      'apply': 'leader',      // Want clear answers to apply right away
      'context': 'thinker',   // Dig into context and connections
      'share': 'promoter',    // Think about sharing it with others
      'reflect': 'nurturer'   // Reflect quietly on what it means
    };
    return personalityMap[behavior] || 'nurturer';
  }, []);





  const screens = useMemo(() => [
    {
      id: 'welcome',
      type: 'welcome',
      title: `Hey ${firstName}, welcome to BibeCheck! 👋`,
      subtitle: "Three quick questions before we start...",
      illustration: Sparkles,
      illustrationColor: "text-purple-500"
    },
    {
      id: 'intent',
      type: 'question',
      title: "What brings you here today?",
      illustration: Sparkles,
      illustrationColor: "text-purple-500",
      options: [
        {
          Icon: Brain,
          iconColor: "text-purple-600",
          text: "I've got questions",
          emoji: "💬",
          value: "questions"
        },
        {
          Icon: Target,
          iconColor: "text-blue-600",
          text: "I need guidance",
          emoji: "🧭",
          value: "guidance"
        },
        {
          Icon: BookOpen,
          iconColor: "text-green-600",
          text: "I want to learn",
          emoji: "📖",
          value: "study"
        },
        {
          Icon: Sparkles,
          iconColor: "text-pink-600",
          text: "I'm just curious",
          emoji: "✨",
          value: "explore"
        }
      ]
    },
    {
      id: 'experience',
      type: 'question',
      title: "Where are you in your Bible journey?",
      illustration: Sprout,
      illustrationColor: "text-green-500",
      options: [
        {
          Icon: Sprout,
          iconColor: "text-green-600",
          text: "Just starting out",
          emoji: "🌱",
          value: "beginner"
        },
        {
          Icon: BookOpen,
          iconColor: "text-blue-600",
          text: "I've read some before",
          emoji: "📖",
          value: "intermediate"
        },
        {
          Icon: Heart,
          iconColor: "text-purple-600",
          text: "I read regularly",
          emoji: "❤️",
          value: "advanced"
        }
      ]
    },
    {
      id: 'behavior',
      type: 'question',
      title: "When you read the Bible, you usually...",
      illustration: BookOpen,
      illustrationColor: "text-blue-500",
      options: [
        {
          Icon: Zap,
          iconColor: "text-orange-600",
          text: "Want answers I can use now",
          emoji: "⚡",
          value: "apply"
        },
        {
          Icon: Brain,
          iconColor: "text-purple-600",
          text: "Want to understand it deeply",
          emoji: "🧠",
          value: "context"
        },
        {
          Icon: Share2,
          iconColor: "text-pink-600",
          text: "Think about sharing it",
          emoji: "💬",
          value: "share"
        },
        {
          Icon: Heart,
          iconColor: "text-green-600",
          text: "Reflect on what it means",
          emoji: "🤍",
          value: "reflect"
        }
      ]
    }
  ], [firstName]);

  // No stats screen - just use base screens
  const allScreens = useMemo(() => {
    return [...screens];
  }, [screens]);
  const currentScreenData = allScreens[currentScreen];
  const currentSelection = selections[currentScreenData.id];

  const handleOptionSelect = (optionValue) => {
    console.log('🔴 CRITICAL DEBUG - handleOptionSelect called:');
    console.log('  Screen ID:', currentScreenData.id);
    console.log('  Screen Title:', currentScreenData.title);
    console.log('  Option Value Clicked:', optionValue);
    console.log('  Current selections BEFORE:', JSON.stringify(selections));

    const newSelections = {
      ...selections,
      [currentScreenData.id]: optionValue
    };

    console.log('  New selections AFTER:', JSON.stringify(newSelections));
    console.log('🔴 END DEBUG');

    setSelections(newSelections);
  };

  const handleNext = () => {
    if (currentScreen < allScreens.length - 1) {
      setCurrentScreen(currentScreen + 1);
    } else {
      // All screens completed - determine personality and pass to parent
      const personality = determinePersonality(selections.behavior);
      onComplete({
        ...selections,
        personality
      });
    }
  };

  const handleBack = () => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  // Check if current screen has a selection (enables Next button)
  // Welcome, info, and story screens can always proceed; question screens need a selection
  const canProceed = 
    currentScreenData.type === 'welcome' || 
    currentScreenData.type === 'info' || 
    currentScreenData.type === 'story' || 
    currentSelection !== null;

  if (!isOpen) return null;

  const IllustrationIcon = currentScreenData.illustration;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-6 right-6 z-10 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={24} />
        </button>

        {/* Content */}
        <div className="p-10 md:p-12 pb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentScreen}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Avatar and Illustration */}
              <div className="mx-auto flex flex-col items-center justify-center mb-6">
                {currentScreenData.type === 'welcome' ? (
                  // Welcome screen - show user avatar
                  <div>
                    {userPhoto ? (
                      <img
                        src={userPhoto}
                        alt={userName || "User"}
                        className="w-24 h-24 rounded-full border-4 border-purple-200 shadow-lg"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full border-4 border-purple-200 shadow-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                        <span className="text-white text-3xl font-bold">
                          {getInitials(userName)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  // Other screens - show icon
                  <>
                    {IllustrationIcon && typeof IllustrationIcon === 'function' ? (
                      <IllustrationIcon
                        className={`w-20 h-20 ${currentScreenData.illustrationColor || 'text-purple-500'}`}
                      />
                    ) : typeof IllustrationIcon === 'string' ? (
                      <span className="text-8xl">{IllustrationIcon}</span>
                    ) : null}
                  </>
                )}
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center leading-relaxed">
                {currentScreenData.title}
              </h2>

              {/* Subtitle (for welcome screen) */}
              {currentScreenData.subtitle && (
                <p className="text-gray-600 text-center mb-10 -mt-6 leading-relaxed">
                  {currentScreenData.subtitle}
                </p>
              )}

              {/* Content based on screen type */}
              {currentScreenData.type === 'welcome' ? (
                // Welcome screen - just title and subtitle, no content
                <div className="mb-10"></div>
              ) : currentScreenData.type === 'story' ? (
                // Story screen - aha moment with before/after
                <div className="space-y-8 mb-10">
                  <div className="text-center">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Meet <span className="font-bold text-purple-600">{currentScreenData.name}</span>, 
                      a {currentScreenData.type} like you...
                    </p>
                  </div>
                  
                  {/* Before */}
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4 text-center">Before BibeCheck</h3>
                    <div className="space-y-3">
                      {currentScreenData.before?.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="text-gray-700"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* After */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-200">
                    <h3 className="font-bold text-purple-900 mb-4 text-center">After 30 Days with BibeCheck</h3>
                    <div className="space-y-3">
                      {currentScreenData.after?.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 + 0.3 }}
                          className="text-purple-900 font-medium"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <p className="text-center text-gray-600 italic">
                    Ready to start your journey?
                  </p>
                </div>
              ) : currentScreenData.type === 'info' ? (
                // Info screen - display static content with personality-specific styling
                <div className="space-y-6 mb-10">
                  {currentScreenData.items?.map((item, index) => {
                    const ItemIcon = item.Icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.15 }}
                        className={`flex items-start gap-5 p-5 bg-gradient-to-r ${currentScreenData.gradient} rounded-2xl border ${currentScreenData.borderColor}`}
                      >
                        {ItemIcon && typeof ItemIcon === 'function' ? (
                          <ItemIcon className={`w-8 h-8 ${item.iconColor} flex-shrink-0 mt-1`} />
                        ) : (
                          <span className="text-4xl flex-shrink-0">{item.icon}</span>
                        )}
                        <div className="flex-1">
                          <div className="text-xl font-bold text-gray-900 mb-1">{item.value}</div>
                          <div className="text-sm text-gray-600 leading-relaxed">{item.label}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                // Question screen - display selectable options
                <div className="space-y-4 mb-10">
                  {currentScreenData.options.map((option, index) => {
                    const OptionIcon = option.Icon;
                    return (
                      <motion.button
                        key={option.value}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleOptionSelect(option.value)}
                        className={`w-full p-5 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
                          currentSelection === option.value
                            ? 'border-purple-600 bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg scale-[1.02]'
                            : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700 hover:shadow-md'
                        }`}
                      >
                        {OptionIcon && typeof OptionIcon === 'function' ? (
                          <OptionIcon 
                            className={`w-8 h-8 flex-shrink-0 ${
                              currentSelection === option.value ? 'text-white' : option.iconColor
                            }`} 
                          />
                        ) : (
                          <span className="text-3xl flex-shrink-0">{option.emoji}</span>
                        )}
                        <span className="font-medium text-base leading-relaxed">{option.text}</span>
                      </motion.button>
                    );
                  })}
                  

                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-8">
            {allScreens.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentScreen
                    ? 'w-8 bg-purple-600'
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3">
            {/* Back button - only show if not on first screen */}
            {currentScreen > 0 && (
              <button
                onClick={handleBack}
                className="flex-1 bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-full hover:bg-gray-300 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <ChevronLeft size={20} />
                <span>Back</span>
              </button>
            )}

            {/* Next/Get Started button */}
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`${currentScreen === 0 ? 'w-full' : 'flex-1'} ${
                canProceed
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                  : 'bg-gray-300 cursor-not-allowed'
              } text-white font-semibold py-4 px-6 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2`}
            >
              <span>{currentScreen === allScreens.length - 1 ? 'Continue' : 'Next'}</span>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingFlow;
